/**
 * Device states Edges can label and colour.
 *
 * `circulate` was half-shipped: its design tokens (`bg-state-circulate`,
 * `text-state-circulate-text` and friends) were generated, but it was in neither
 * this union nor `badgeVariantStyles`, so the 23,330 `fanMode` events a week that
 * report it had no styling to resolve. Adding it here is what wires the tokens up.
 *
 * The two `Record<DeviceState, …>` maps below are the completeness guard: adding a
 * member breaks the build until both have an entry. `activeDeviceStates` is a plain
 * array and is NOT compiler-enforced — decide about it explicitly.
 */
export type DeviceState =
  | "unknown"
  | "on"
  | "off"
  | "idle"
  | "charging"
  | "discharging"
  | "heat"
  | "eco"
  | "cool"
  | "auto"
  | "circulate"
  | "connected"
  | "disconnected";

export type MetricFormat = "percentage" | "kW" | "temperature";

export const deviceStateMetricFormats: Record<DeviceState, MetricFormat | undefined> = {
  charging: "percentage",
  discharging: "percentage",
  on: "kW",
  idle: undefined,
  off: undefined,
  heat: "temperature",
  cool: "temperature",
  auto: "temperature",
  eco: "temperature",
  // A circulating fan reports no metric of its own — the thermostat's temperature
  // belongs to its operating mode, not to the fan.
  circulate: undefined,
  unknown: undefined,
  connected: undefined,
  disconnected: undefined,
} as const;

/**
 * Device states that represent active/operational conditions
 * Used for pulsing indicators, priority sorting, etc.
 */
export const activeDeviceStates: readonly DeviceState[] = [
  "charging",
  "discharging",
  "heat",
  "cool",
  "on",
  // Included deliberately. This array drives the pulsing dot, and every other
  // member is "the device is doing work right now" — a circulating fan is running,
  // which is exactly that, even though it runs intermittently. This list is not
  // compiler-enforced, so the choice is recorded rather than inferred.
  "circulate",
  "connected",
] as const;

/**
 * Human-readable labels for device states
 */
export const deviceStateLabels: Record<DeviceState, string> = {
  unknown: "Unknown",
  on: "On",
  off: "Off",
  idle: "Idle",
  charging: "Charging",
  discharging: "Discharging",
  heat: "Heating",
  eco: "Eco Mode",
  cool: "Cooling",
  auto: "Auto",
  circulate: "Circulating",
  connected: "Connected",
  disconnected: "Disconnected",
} as const;

/**
 * Check if a device state represents an active/operational state
 */
export function isActiveState(state: DeviceState): boolean {
  return activeDeviceStates.includes(state);
}

/**
 * Get human-readable label for a device state
 */
export function getDeviceStateLabel(state: DeviceState): string {
  return deviceStateLabels[state];
}
